import React, { useEffect, useRef } from 'react';

// Red de partículas (estilo particles.js) hecha con canvas, sin dependencias.
// Se pausa fuera de pantalla o con la pestaña oculta. Se anima siempre, también con "reducir movimiento".
const COLORS = ['56, 189, 248', '34, 211, 238', '167, 139, 250', '148, 163, 184'];

const ParticlesBackground = ({ density = 1, linkDistance = 140, interactive = true, className = '' }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const mouse = { x: null, y: null };
    let particles = [];
    let width = 0;
    let height = 0;
    let frame = null;
    let visible = true;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      // Cantidad según el área: menos partículas en pantallas chicas.
      const count = Math.round(Math.min(110, (width * height) / 11000) * density);
      particles = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.35,
        vy: (Math.random() - 0.5) * 0.35,
        r: Math.random() * 1.8 + 0.8,
        c: COLORS[Math.floor(Math.random() * COLORS.length)],
      }));
    };

    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      for (let i = 0; i < particles.length; i += 1) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > width) p.vx *= -1;
        if (p.y < 0 || p.y > height) p.vy *= -1;
        for (let j = i + 1; j < particles.length; j += 1) {
          const q = particles[j];
          const dist = Math.hypot(p.x - q.x, p.y - q.y);
          if (dist < linkDistance) {
            ctx.strokeStyle = `rgba(148, 197, 255, ${0.22 * (1 - dist / linkDistance)})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(q.x, q.y);
            ctx.stroke();
          }
        }
        if (interactive && mouse.x !== null) {
          const dm = Math.hypot(p.x - mouse.x, p.y - mouse.y);
          if (dm < 180) {
            ctx.strokeStyle = `rgba(34, 211, 238, ${0.5 * (1 - dm / 180)})`;
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(mouse.x, mouse.y);
            ctx.stroke();
          }
        }
        ctx.fillStyle = `rgba(${p.c}, 0.85)`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    const loop = () => {
      draw();
      frame = visible && !document.hidden ? requestAnimationFrame(loop) : null;
    };
    const start = () => { if (!frame) frame = requestAnimationFrame(loop); };

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
    };
    const onLeave = () => { mouse.x = null; mouse.y = null; };
    const onVisibility = () => { if (!document.hidden) start(); };

    resize();
    draw();
    start();

    const host = canvas.parentElement;
    const io = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible) start();
    });
    io.observe(canvas);
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    if (interactive) {
      host.addEventListener('mousemove', onMove);
      host.addEventListener('mouseleave', onLeave);
    }
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      io.disconnect();
      ro.disconnect();
      host.removeEventListener('mousemove', onMove);
      host.removeEventListener('mouseleave', onLeave);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [density, linkDistance, interactive]);

  return <canvas ref={canvasRef} className={`lp-particles ${className}`} aria-hidden="true" />;
};

export default ParticlesBackground;
